import { useRef, useMemo } from 'react';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import { logger } from '@utils/usefulFuncs';

const parser = unified().use(remarkParse);

/**
 * Splits a growing markdown string into frozen (already-parsed) chunks and an active tail.
 *
 * During streaming, the answer string grows incrementally. Without chunking, react-markdown
 * re-parses the entire string (including all rehype/remark plugins) on every tick. This hook
 * uses remark-parse (lightweight) to identify completed top-level AST nodes and freezes them
 * so they are only rendered once. Only the trailing incomplete node is re-rendered each tick.
 *
 * @param {string|null} answer - The markdown answer string (grows during streaming)
 * @returns {{ frozenChunks: Array<{ id: string, markdown: string }>, activeTail: string }}
 */
export const useChunkedMarkdown = (answer, isStreaming) => {
  const frozenChunksRef = useRef([]);
  const frozenEndOffsetRef = useRef(0);

  return useMemo(() => {
    if (!answer) {
      frozenChunksRef.current = [];
      frozenEndOffsetRef.current = 0;
      return { frozenChunks: [], activeTail: '' };
    }

    // Skip chunking when not streaming — render the full answer in a single pass
    if (!isStreaming) {
      frozenChunksRef.current = [];
      frozenEndOffsetRef.current = 0;
      return { frozenChunks: [], activeTail: answer };
    }

    // Reset if the answer shrunk (new query replaced the previous one)
    if (answer.length < frozenEndOffsetRef.current) {
      frozenChunksRef.current = [];
      frozenEndOffsetRef.current = 0;
    }

    // Parse into mdast AST to find top-level block boundaries.
    // remark-parse is lightweight compared to the full rehype pipeline (katex, highlight, etc.)
    const tree = parser.parse(answer);
    const nodes = tree.children;

    if (nodes.length <= 1) {
      // Single or zero top-level nodes — nothing to freeze yet
      return { frozenChunks: frozenChunksRef.current, activeTail: answer };
    }

    // The last top-level node is always "active" since more content could extend it
    // (e.g. a list could gain more items, a table more rows). All preceding nodes are complete.
    const completedNodeCount = nodes.length - 1;
    const currentFrozenCount = frozenChunksRef.current.length;

    if (completedNodeCount > currentFrozenCount) {
      const newChunks = [];
      for (let i = currentFrozenCount; i < completedNodeCount; i++) {
        const node = nodes[i];
        newChunks.push({
          id: `chunk-${i}`,
          markdown: answer.substring(node.position.start.offset, node.position.end.offset),
        });
      }
      frozenChunksRef.current = [...frozenChunksRef.current, ...newChunks];
      newChunks.forEach((chunk, i) => {
        logger('useChunkedMarkdown: chunk frozen', currentFrozenCount + i + 1, chunk.markdown);
      });

      const lastFrozenNode = nodes[completedNodeCount - 1];
      frozenEndOffsetRef.current = lastFrozenNode.position.end.offset;
    }

    const activeTail = answer.substring(frozenEndOffsetRef.current);

    return {
      frozenChunks: frozenChunksRef.current,
      activeTail,
    };
  }, [answer, isStreaming]);
};
